/**
 * useChronicleUsbMode — provides toggle for USB mass storage mode.
 *
 * USB mode state (`usb_mode_enabled`) is now pushed reliably from the device
 * via `on_notify_line` handling of `MCU&USB&{0|1}` (T200). No frontend
 * polling is needed — state arrives via `chronicle:state_changed` events.
 *
 * This hook only provides the toggle command and toggling state.
 */
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useRef, useState } from 'react';

import {
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
} from '@/atoms/chronicle';
import { chronicleBridge } from '@/tauri/chronicle';
import {
  trackHardwareUsbModeToggleFailed,
  trackHardwareUsbModeToggled,
} from '@/utils/analytics/track-hardware-events';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

export function useChronicleUsbMode() {
  const [isToggling, setIsToggling] = useState(false);
  const isTogglingRef = useRef(false);
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const connectionContext = useAtomValue(chronicleConnectionContextAtom);

  const toggleUsbMode = useCallback(
    async (enabled: boolean) => {
      if (isTogglingRef.current) return;
      isTogglingRef.current = true;
      setIsToggling(true);

      setDeviceState((prev) => ({ ...prev, usbModeEnabled: enabled }));

      const deviceId = connectionContext.linkedDeviceId ?? '';

      try {
        const confirmedValue = await chronicleBridge.setUsbMode(enabled);
        setDeviceState((prev) => ({
          ...prev,
          usbModeEnabled: confirmedValue ?? enabled,
        }));
        trackHardwareUsbModeToggled({
          enabled: confirmedValue ?? enabled,
          deviceId,
        });
      } catch (error) {
        setDeviceState((prev) => ({ ...prev, usbModeEnabled: !enabled }));
        logger.error({
          msg: '[useChronicleUsbMode] Failed to set USB mode',
          error: error instanceof Error ? error.message : String(error),
        });
        trackHardwareUsbModeToggleFailed({
          deviceId,
          errorDescription: error instanceof Error ? error.message : String(error),
        });
        throw error;
      } finally {
        isTogglingRef.current = false;
        setIsToggling(false);
      }
    },
    [setDeviceState, connectionContext.linkedDeviceId]
  );

  return { toggleUsbMode, isToggling };
}
